import React, {
  RefObject,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ClipboardPaste,
  Clock3,
  ContactRound,
  Delete,
  Forward,
  History,
  Keyboard,
  LoaderCircle,
  Mic,
  MicOff,
  Pause,
  Phone,
  PhoneCall,
  PhoneIncoming,
  PhoneMissed,
  PhoneOff,
  PhoneOutgoing,
  Play,
  RefreshCw,
  Search,
  UserRound,
  X,
  type LucideIcon,
} from 'lucide-react'
import { useDispatcher } from '@/providers/DispatcherProvider'
import {
  type SoftphoneActiveCall,
  type SoftphonePanelTab,
  useSoftphone,
} from '@/providers/SoftphoneProvider'
import type { SoftphoneRealtimeStatus } from '@/services/softphone/realtimeSocket'
import {
  useActiveSoftphoneCalls,
  useSoftphoneCallHistory,
  useSoftphoneQueueStatus,
} from '@/hooks/api/useSoftphone'
import { usePatientEntityLookups } from '@/hooks/api/usePatientEntities'
import { useGetAllDispatchers } from '@/hooks/api/useDispatcher'
import { useShifts } from '@/hooks/api/useShifts'
import { useAllResponders } from '@/hooks/api/useResponder'
import { useAllHospitals } from '@/hooks/api/useHospital'
import { useAmbulanceProviders } from '@/hooks/api/useAmbulance'
import { useDebounce } from '@/hooks/useDebounce'
import type {
  SoftphoneCallDirection,
  SoftphoneCallSession,
  SoftphoneQueueStatus,
} from '@/types/api/softphone'
import type { PatientEntity } from '@/types/api/patients'
import type { Responder } from '@/types/api/responders'

type HistoryFilter = 'all' | 'incoming' | 'outgoing' | 'missed'
type ContactTab = 'all' | 'responders' | 'hospitals' | 'ambulances'

interface SoftphoneContact {
  id: string
  name: string
  phoneNumber?: string
  type: ContactTab
  subtitle?: string
  status?: string
  responder?: Responder
}

interface SoftphoneContactLookup {
  byId: Map<string, string>
  byPhone: Map<string, string>
}

const topTabs: Array<{
  key: SoftphonePanelTab
  label: string
  Icon: LucideIcon
}> = [
  { key: 'history', label: 'Call History', Icon: History },
  { key: 'dialpad', label: 'Dialpad', Icon: Keyboard },
  { key: 'contacts', label: 'Contacts', Icon: ContactRound },
]

const historyFilters: Array<{ key: HistoryFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'incoming', label: 'Incoming' },
  { key: 'outgoing', label: 'Outgoing' },
  { key: 'missed', label: 'Missed' },
]

const getRealtimeStatusMessage = (
  status: SoftphoneRealtimeStatus,
  error?: string | null
) => {
  switch (status) {
    case 'connected':
      return null
    case 'connecting':
      return 'Connecting softphone realtime events'
    case 'disconnected':
    case 'error':
      return 'Softphone realtime reconnecting'
    case 'unavailable':
      return error || 'Softphone realtime unavailable'
    case 'idle':
    default:
      return 'Preparing softphone realtime events'
  }
}

const humanizeSoftphoneMessage = (message?: string | null) => {
  if (!message) return null

  const normalized = message.toLowerCase()

  if (
    normalized.includes('sip invite rejected') &&
    (normalized.includes('404') || normalized.includes('cause=3'))
  ) {
    return 'Call route not found. The softphone tried the available route, but Asterisk does not have a matching destination.'
  }

  if (
    normalized.includes('sip invite rejected') &&
    (normalized.includes('480') ||
      normalized.includes('temporarily unavailable'))
  ) {
    return 'The callee is temporarily unavailable. Try again or use another contact route.'
  }

  if (
    normalized.includes('sip invite rejected') &&
    (normalized.includes('486') || normalized.includes('busy'))
  ) {
    return 'The callee is busy.'
  }

  if (
    normalized.includes('sip invite rejected') &&
    (normalized.includes('408') || normalized.includes('timeout'))
  ) {
    return 'The call timed out before the destination answered.'
  }

  if (normalized.includes('softphone is not connected')) {
    return 'Softphone is disconnected. Reconnect before placing a call.'
  }

  if (normalized.includes('softphone is still connecting')) {
    return 'Softphone is still connecting.'
  }

  if (normalized.includes('session does not exist')) {
    return 'The call session has already ended.'
  }

  if (normalized.includes('request failed with status code 502')) {
    return 'Call service is temporarily unavailable.'
  }

  if (normalized.includes('request failed with status code 504')) {
    return 'Call service timed out.'
  }

  if (normalized.includes('dispatcher is not on an active shift')) {
    return 'Softphone access is blocked because this dispatcher is not on an active shift.'
  }

  if (
    normalized.includes('failed to fetch') ||
    normalized.includes('network')
  ) {
    return 'Network connection lost. Check connectivity and try again.'
  }

  return message
}

const dialpadKeys = [
  { digit: '1', letters: '' },
  { digit: '2', letters: 'ABC' },
  { digit: '3', letters: 'DEF' },
  { digit: '4', letters: 'GHI' },
  { digit: '5', letters: 'JKL' },
  { digit: '6', letters: 'MNO' },
  { digit: '7', letters: 'PQRS' },
  { digit: '8', letters: 'TUV' },
  { digit: '9', letters: 'WXYZ' },
  { digit: '*', letters: '' },
  { digit: '0', letters: '+' },
  { digit: '#', letters: '' },
]

const SOFTPHONE_PANEL_GAP = 8
const SOFTPHONE_PANEL_WIDTH = 472
const SOFTPHONE_PANEL_MAX_HEIGHT = 880
const SOFTPHONE_VIEWPORT_MARGIN = 16

const formatDialpadNumber = (value: string) => {
  if (!value) return 'Enter Number'

  const compactValue = value.replace(/\s+/g, '')
  const groups: string[] = []
  let cursor = 0
  const groupPattern = [4, 3, 4]

  groupPattern.forEach(size => {
    if (cursor >= compactValue.length) return
    groups.push(compactValue.slice(cursor, cursor + size))
    cursor += size
  })

  if (cursor < compactValue.length) {
    groups.push(compactValue.slice(cursor))
  }

  return groups.join(' ')
}

const formatPersonName = (
  firstName?: string | null,
  lastName?: string | null,
  fallback?: string
) => {
  const name = [firstName, lastName].filter(Boolean).join(' ').trim()
  return name || fallback || ''
}

const getOptionalStringField = (source: unknown, field: string) => {
  if (!source || typeof source !== 'object') return undefined

  const value = (source as Record<string, unknown>)[field]
  return typeof value === 'string' ? value : undefined
}

const normalizePhoneForLookup = (value?: string | null) =>
  String(value || '').replace(/\D/g, '')

const getEntityPrecinctIds = (source: unknown) => {
  if (!source || typeof source !== 'object') return []

  const record = source as Record<string, unknown>
  const ids = new Set<string>()
  const addId = (value: unknown) => {
    if (typeof value === 'string' && value.trim()) ids.add(value.trim())
  }

  addId(record.precinct_id)
  addId(record.precinctId)
  addId(record.dispatch_center_id)
  addId(record.dispatch_center_precinct_id)

  if (Array.isArray(record.working_precincts)) {
    record.working_precincts.forEach(addId)
  }

  if (Array.isArray(record.coverage_by_precinct)) {
    record.coverage_by_precinct.forEach(item => {
      if (item && typeof item === 'object') {
        addId((item as Record<string, unknown>).precinct_id)
      }
    })
  }

  return Array.from(ids)
}

const matchesCurrentPrecinct = (
  source: unknown,
  currentPrecinctId?: string | null
) => {
  if (!currentPrecinctId) return true

  const precinctIds = getEntityPrecinctIds(source)
  return precinctIds.includes(currentPrecinctId)
}

const getPhoneLookupKeys = (value?: string | null) => {
  const digits = normalizePhoneForLookup(value)
  if (!digits) return []

  const keys = new Set<string>([digits])
  if (digits.length >= 10) keys.add(digits.slice(-10))
  if (digits.startsWith('234') && digits.length > 10) {
    keys.add(`0${digits.slice(3)}`)
  }
  if (digits.startsWith('0') && digits.length > 1) {
    keys.add(`234${digits.slice(1)}`)
  }

  return Array.from(keys)
}

const getSipUserPart = (value?: string | null) => {
  const normalized = String(value || '')
    .trim()
    .replace(/^PJSIP\//i, '')
    .replace(/^sip:/i, '')
  return normalized.split('@')[0] || ''
}

const addContactLookupEntry = (
  lookup: SoftphoneContactLookup,
  {
    id,
    phoneNumber,
    name,
  }: {
    id?: string | null
    phoneNumber?: string | null
    name?: string | null
  }
) => {
  const cleanName = String(name || '').trim()
  if (!cleanName) return

  if (id) lookup.byId.set(String(id), cleanName)
  getPhoneLookupKeys(phoneNumber).forEach(key => {
    lookup.byPhone.set(key, cleanName)
  })
}

const isGenericSoftphoneName = (value?: string | null) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
  return (
    !normalized ||
    normalized === 'unknown' ||
    normalized === 'unknown caller' ||
    normalized === 'era dispatcher' ||
    normalized === 'toll-free'
  )
}

const resolvePhoneContactName = (
  value: string | null | undefined,
  lookup: SoftphoneContactLookup
) => {
  const sipUser = getSipUserPart(value)
  const candidates = [sipUser, value]

  for (const candidate of candidates) {
    for (const key of getPhoneLookupKeys(candidate)) {
      const match = lookup.byPhone.get(key)
      if (match) return match
    }
  }

  return undefined
}

const getActiveCallContactDisplay = (
  call: SoftphoneActiveCall,
  lookup: SoftphoneContactLookup
) => {
  const lookupName =
    resolvePhoneContactName(call.phoneNumber, lookup) ||
    resolvePhoneContactName(call.target, lookup)
  const displayName = isGenericSoftphoneName(call.displayName)
    ? undefined
    : call.displayName
  const targetName = isGenericSoftphoneName(call.target)
    ? undefined
    : call.target
  const rawTarget =
    call.phoneNumber ||
    (!isGenericSoftphoneName(call.target) ? call.target : undefined)
  const name =
    lookupName || displayName || targetName || rawTarget || 'Unknown Caller'
  const detail =
    rawTarget && rawTarget !== name && rawTarget !== lookupName
      ? rawTarget
      : undefined

  return { name, detail }
}

const getDirection = (call: SoftphoneCallSession): SoftphoneCallDirection => {
  if (call.direction === 'incoming' || call.direction === 'inbound') {
    return 'incoming'
  }

  if (call.direction === 'outgoing' || call.direction === 'outbound') {
    return 'outgoing'
  }

  if (call.status === 'missed') return 'incoming'
  if (call.to_type === 'pstn' || call.to_user_type === 'responder') {
    return 'outgoing'
  }
  if (call.to || call.dial_target || call.to_user_id) return 'outgoing'

  return 'incoming'
}

const resolveCallContactName = (
  call: SoftphoneCallSession,
  lookup: SoftphoneContactLookup
) => {
  const direction = getDirection(call)
  const idCandidates =
    direction === 'outgoing'
      ? [call.to_user_id, call.to, getSipUserPart(call.dial_target)]
      : [call.from_user_id, call.from, getSipUserPart(call.from)]

  for (const id of idCandidates) {
    const match = id ? lookup.byId.get(String(id)) : undefined
    if (match) return match
  }

  const phoneCandidates =
    direction === 'outgoing'
      ? [call.to, call.phone_number, call.dial_target, call.sip_uri]
      : [call.phone_number, call.from, call.caller_id, call.callerId]

  for (const phone of phoneCandidates) {
    const sipUser = getSipUserPart(phone)
    for (const key of getPhoneLookupKeys(sipUser || phone)) {
      const match = lookup.byPhone.get(key)
      if (match) return match
    }
  }

  return getDisplayTarget(call)
}

const resolveCallResponderName = (
  call: SoftphoneCallSession,
  lookup: SoftphoneContactLookup
) => {
  const assignedId = call.assigned_to_actor_id
  if (!assignedId) return ''

  return (
    lookup.byId.get(String(assignedId)) ||
    resolvePhoneContactName(call.assigned_to_phone_number, lookup) ||
    call.assigned_to_phone_number ||
    String(assignedId)
  )
}

const getDisplayTarget = (call: SoftphoneCallSession) => {
  if (getDirection(call) === 'outgoing') {
    return (
      call.to ||
      call.to_user_id ||
      call.dial_target ||
      call.sip_uri ||
      call.caller_id ||
      call.callerId ||
      'Unknown'
    )
  }

  return (
    call.phone_number ||
    call.caller_id ||
    call.callerId ||
    call.from ||
    call.from_user_id ||
    call.to ||
    call.to_user_id ||
    call.dial_target ||
    'Unknown'
  )
}

const getCallPhoneCandidates = (call: SoftphoneCallSession) => {
  const direction = getDirection(call)
  const candidates =
    direction === 'outgoing'
      ? [call.to, call.phone_number, call.dial_target, call.sip_uri]
      : [call.phone_number, call.from, call.caller_id, call.callerId]

  return candidates
    .flatMap(value => [value, getSipUserPart(value)])
    .filter((value): value is string => Boolean(value))
}

const getCallableHistoryNumber = (call: SoftphoneCallSession) => {
  const candidates = getCallPhoneCandidates(call)

  for (const candidate of candidates) {
    const normalized = normalizePhoneForLookup(candidate)
    if (normalized.length >= 7) return candidate
  }

  return ''
}

const getActiveCallPhoneCandidates = (call?: SoftphoneActiveCall | null) =>
  call ? [call.phoneNumber, call.target].filter(Boolean) : []

const getPatientDisplayName = (patient: PatientEntity) => {
  const names = patient.names
    .map(item => item.name)
    .filter(Boolean)
    .filter((name, index, all) => all.indexOf(name) === index)

  return names.join(' / ') || patient.display_name || 'Patient'
}

const getCallTime = (call: SoftphoneCallSession) => {
  const timestamp = Date.parse(call.started_at || call.created_at || '')
  return Number.isNaN(timestamp) ? 0 : timestamp
}

const getCallCorrelationTarget = (
  call: SoftphoneCallSession,
  lookup: SoftphoneContactLookup
) => {
  const direction = getDirection(call)
  const phoneCandidates =
    direction === 'outgoing'
      ? [call.to, call.phone_number, call.dial_target, call.sip_uri]
      : [call.phone_number, call.from, call.caller_id, call.callerId]

  for (const phone of phoneCandidates) {
    const sipUser = getSipUserPart(phone)
    const keys = getPhoneLookupKeys(sipUser || phone)
    if (keys.length > 0) return keys[0]
  }

  return resolveCallContactName(call, lookup).trim().toLowerCase()
}

const getCallStatusRank = (call: SoftphoneCallSession) => {
  const status = String(call.status || '').toLowerCase()
  if (['completed', 'answered', 'held'].includes(status)) return 5
  if (['dialing', 'ringing', 'initiating'].includes(status)) return 4
  if (status === 'rejected') return 3
  if (['failed', 'cancelled', 'canceled', 'no_answer'].includes(status))
    return 2
  if (status === 'missed') return 1
  return 0
}

const isUnsuccessfulCallStatus = (status?: string) =>
  [
    'missed',
    'rejected',
    'failed',
    'cancelled',
    'canceled',
    'no_answer',
  ].includes(String(status || '').toLowerCase())

const isSuccessfulCallStatus = (status?: string) =>
  ['completed', 'answered', 'held'].includes(String(status || '').toLowerCase())

const isLocalOnlyCall = (call: SoftphoneCallSession) =>
  call.call_id.startsWith('local-') || call.metadata?.localOnly === true

const shouldReplaceHistoryCall = (
  current: SoftphoneCallSession,
  next: SoftphoneCallSession
) => {
  if (isLocalOnlyCall(current) !== isLocalOnlyCall(next)) {
    return isLocalOnlyCall(current)
  }

  const statusDifference = getCallStatusRank(next) - getCallStatusRank(current)
  if (statusDifference !== 0) return statusDifference > 0

  return getCallTime(next) > getCallTime(current)
}

const dedupeHistoryCalls = (
  calls: SoftphoneCallSession[],
  lookup: SoftphoneContactLookup
) => {
  const deduped: SoftphoneCallSession[] = []

  calls.forEach(call => {
    const direction = getDirection(call)
    const target = getCallCorrelationTarget(call, lookup)
    const time = getCallTime(call)
    const duplicateIndex = deduped.findIndex(existing => {
      if (direction !== 'outgoing' || getDirection(existing) !== 'outgoing') {
        return false
      }
      if (!target || target !== getCallCorrelationTarget(existing, lookup)) {
        return false
      }

      return Math.abs(time - getCallTime(existing)) <= 45 * 1000
    })

    if (duplicateIndex === -1) {
      deduped.push(call)
      return
    }

    if (shouldReplaceHistoryCall(deduped[duplicateIndex], call)) {
      deduped[duplicateIndex] = call
    }
  })

  return deduped
}

const useSoftphoneContactLookup = (
  patientPhoneNumbers: Array<string | null | undefined> = [],
  patientLookupEnabled = true
) => {
  const { dispatcher } = useDispatcher()
  const currentPrecinctId = dispatcher?.precinct_id
  const { data: dispatchersData } = useGetAllDispatchers()
  const { data: respondersData } = useAllResponders({
    pageNumber: 1,
    numberPerPage: 100,
    precinct_id: currentPrecinctId,
  })
  const { data: hospitals = [] } = useAllHospitals({
    pageNumber: 1,
    numberPerPage: 100,
    precinct_id: currentPrecinctId,
  })
  const { data: ambulanceData } = useAmbulanceProviders({
    limit: 100,
    precinct_id: currentPrecinctId,
  })
  const patientLookupResult = usePatientEntityLookups(
    patientPhoneNumbers,
    patientLookupEnabled
  )
  const patients = useMemo(
    () =>
      Object.values(patientLookupResult.data?.patients_by_contact_number || {})
        .map(result => result.patient)
        .filter((patient): patient is PatientEntity => Boolean(patient)),
    [patientLookupResult.data?.patients_by_contact_number]
  )

  return useMemo<SoftphoneContactLookup>(() => {
    const lookup: SoftphoneContactLookup = {
      byId: new Map(),
      byPhone: new Map(),
    }

    if (dispatcher) {
      addContactLookupEntry(lookup, {
        id: dispatcher.dispatcher_id,
        phoneNumber: getOptionalStringField(dispatcher, 'phone_number'),
        name: formatPersonName(
          dispatcher.first_name,
          dispatcher.last_name,
          'Current Dispatcher'
        ),
      })
    }

    const dispatchers =
      dispatchersData?.data?.dispatchers ||
      dispatchersData?.dispatchers ||
      dispatchersData?.items ||
      []
    dispatchers.forEach((item: any) => {
      if (!matchesCurrentPrecinct(item, currentPrecinctId)) return

      addContactLookupEntry(lookup, {
        id: item.dispatcher_id || item.user_id,
        phoneNumber: item.phone_number,
        name: formatPersonName(item.first_name, item.last_name, item.email),
      })
    })

    respondersData?.items
      ?.filter(responder =>
        matchesCurrentPrecinct(responder, currentPrecinctId)
      )
      .forEach(responder => {
        const responderName = formatPersonName(
          responder.first_name,
          responder.last_name,
          responder.phone_number || 'Responder'
        )

        addContactLookupEntry(lookup, {
          id: responder.responder_id,
          phoneNumber: responder.phone_number,
          name: responderName,
        })
        addContactLookupEntry(lookup, {
          phoneNumber: getOptionalStringField(
            responder,
            'alternative_phone_number'
          ),
          name: responderName,
        })
      })

    hospitals
      .filter((hospital: any) =>
        matchesCurrentPrecinct(hospital, currentPrecinctId)
      )
      .forEach((hospital: any) => {
        addContactLookupEntry(lookup, {
          id: hospital.hospital_id || hospital.id,
          phoneNumber:
            hospital.phoneNumber ||
            hospital.phone_number ||
            hospital.contact?.phone ||
            hospital.contact_phone,
          name: hospital.name || hospital.hospital_name || 'Hospital',
        })
      })

    const ambulanceProviders = Array.isArray((ambulanceData as any)?.items)
      ? (ambulanceData as any).items
      : Array.isArray((ambulanceData as any)?.ambulance_providers)
        ? (ambulanceData as any).ambulance_providers
        : Array.isArray((ambulanceData as any)?.providers)
          ? (ambulanceData as any).providers
          : Array.isArray(ambulanceData)
            ? ambulanceData
            : []
    ambulanceProviders
      .filter((provider: any) =>
        matchesCurrentPrecinct(provider, currentPrecinctId)
      )
      .forEach((provider: any) => {
        addContactLookupEntry(lookup, {
          id:
            provider.provider_id ||
            provider.ambulance_provider_id ||
            provider.id,
          phoneNumber:
            provider.contact_phone_number ||
            provider.phone_number ||
            provider.contact_phone ||
            provider.dispatch_phone ||
            provider.dispatch_phone_number ||
            provider.contact?.phone,
          name:
            provider.provider_name ||
            provider.company_name ||
            provider.name ||
            'Ambulance Provider',
        })
      })

    patients.forEach(patient => {
      const patientName = getPatientDisplayName(patient)
      const patientNumbers = [
        patient.contact_number,
        patient.identifier,
        ...(patient.contact_numbers || []),
      ]

      patientNumbers.forEach(phoneNumber => {
        addContactLookupEntry(lookup, {
          phoneNumber,
          name: patientName,
        })
      })
    })

    return lookup
  }, [
    ambulanceData,
    currentPrecinctId,
    dispatcher,
    dispatchersData,
    hospitals,
    patients,
    respondersData?.items,
  ])
}

const getCallCategory = (call: SoftphoneCallSession) => {
  if (call.to_type === 'pstn') return 'PSTN'
  if (call.to_user_type === 'responder') return 'Responder'
  if (call.to_type === 'user') return 'SIP'
  return getDirection(call) === 'outgoing' ? 'Outgoing' : 'Incoming'
}

const getCallStatusLabel = (call: SoftphoneCallSession) => {
  const status = String(call.status || '').toLowerCase()
  switch (status) {
    case 'initiating':
      return 'Starting'
    case 'dialing':
      return 'Dialing'
    case 'ringing':
      return 'Ringing'
    case 'answered':
      return 'Answered'
    case 'held':
      return 'Held'
    case 'completed':
      return 'Complete'
    case 'rejected':
      return 'Rejected'
    case 'missed':
      return 'Missed'
    case 'cancelled':
    case 'canceled':
      return 'Cancelled'
    case 'no_answer':
      return 'No answer'
    case 'failed':
      return 'Failed'
    default:
      return status ? status.replace(/_/g, ' ') : 'Logged'
  }
}

const getCallCategoryClasses = (call: SoftphoneCallSession) => {
  if (isUnsuccessfulCallStatus(call.status)) {
    return 'border-[#ffd6dc] bg-[#fff6f7] text-[#d83a4a]'
  }
  if (isSuccessfulCallStatus(call.status)) {
    return 'border-[#d9f3e4] bg-[#f3fff8] text-[#0d8f45]'
  }
  if (call.to_type === 'pstn') {
    return 'border-[#d8e8ff] bg-[#f4f8ff] text-primary'
  }
  if (call.to_user_type === 'responder') {
    return 'border-[#d9f3e4] bg-[#f3fff8] text-[#0d8f45]'
  }
  return 'border-[#e5e7eb] bg-[#f8fafc] text-[#4b5563]'
}

const getHistoryIcon = (call: SoftphoneCallSession): React.ReactElement => {
  if (isUnsuccessfulCallStatus(call.status)) {
    return <PhoneMissed size={22} className="text-[#ff5263]" />
  }

  const iconClass = isSuccessfulCallStatus(call.status)
    ? 'text-[#12a84f]'
    : 'text-primary'

  return getDirection(call) === 'outgoing' ? (
    <PhoneOutgoing size={22} className={iconClass} />
  ) : (
    <PhoneIncoming size={22} className={iconClass} />
  )
}

const formatTime = (dateValue?: string) => {
  if (!dateValue) return ''
  const date = new Date(dateValue)
  if (Number.isNaN(date.getTime())) return ''

  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  })
}

const formatRelative = (dateValue?: string) => {
  if (!dateValue) return ''
  const date = new Date(dateValue)
  const diffMs = Date.now() - date.getTime()
  if (Number.isNaN(diffMs)) return ''

  const minutes = Math.max(0, Math.floor(diffMs / 60000))
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}min${minutes === 1 ? '' : 's'} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

const formatDuration = (seconds?: number) => {
  if (!seconds || seconds < 0) return '0:00'
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60

  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60)
    const remainderMinutes = minutes % 60
    return `${hours}h ${remainderMinutes}min`
  }

  return `${minutes} min${minutes === 1 ? '' : 's'} ${remainingSeconds}sec`
}

const getHistoryDetail = (call: SoftphoneCallSession, startedAt?: string) => {
  const timestamp = [
    formatRelative(startedAt),
    startedAt ? formatTime(startedAt) : '',
  ]
    .filter(Boolean)
    .join(' - ')

  return [timestamp, formatDuration(call.duration_seconds || call.duration)]
    .filter(Boolean)
    .join(' - ')
}

const useCallTimer = (call: SoftphoneActiveCall | null) => {
  const [now, setNow] = useState(() => Date.now())

  React.useEffect(() => {
    if (!call?.answeredAt) return
    const interval = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(interval)
  }, [call?.answeredAt])

  if (!call?.answeredAt) return '00:00'
  const elapsed = Math.max(
    0,
    Math.floor((now - Date.parse(call.answeredAt)) / 1000)
  )
  const minutes = Math.floor(elapsed / 60)
  const seconds = elapsed % 60
  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`
}

const EmptyState = ({
  title,
  subtitle,
  icon = <Phone size={48} className="text-[#969696]" />,
}: {
  title: string
  subtitle: string
  icon?: React.ReactNode
}) => (
  <div className="flex h-full flex-col items-center justify-center text-center">
    <div className="mb-5">{icon}</div>
    <p className="text-sm font-bold text-[#333333]">{title}</p>
    <p className="mt-2 text-sm text-off-black">{subtitle}</p>
  </div>
)

const SoftphoneTopTabs = () => {
  const { activeTab, setActiveTab } = useSoftphone()

  return (
    <div className="mx-4 mt-3 grid shrink-0 grid-cols-3 gap-2 rounded bg-[#F5F5F7] p-1">
      {topTabs.map(({ key, label, Icon }) => {
        const active = activeTab === key
        return (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex h-8 items-center justify-center gap-2 rounded-lg text-xs transition-colors cursor-pointer ${
              active
                ? 'border border-primary bg-white font-semibold text-primary'
                : 'text-[#777777] hover:text-primary hover:bg-blue-100/50'
            }`}
            style={{
              color: active ? '#254769' : '#2C2C2C',
              fontWeight: active ? 500 : 400,
              fontSize: '14px',
            }}
          >
            <Icon size={15} />
            <span>{label}</span>
          </button>
        )
      })}
    </div>
  )
}

const SearchBox = ({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) => (
  <div className="mx-4 mt-3 flex h-10 shrink-0 items-center gap-2 rounded-lg border border-[#939090] px-3">
    <Search size={16} className="text-[#939090] -mt-px" />
    <input
      value={value}
      onChange={event => onChange(event.target.value)}
      placeholder="Search..."
      className="h-full min-w-0 flex-1 bg-transparent outline-none"
      style={{ fontSize: '14px' }}
    />
  </div>
)

const HistoryView = () => {
  const { dispatcher } = useDispatcher()
  const { attemptedCalls, isOpen, isEligible, startDialpadCall } =
    useSoftphone()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<HistoryFilter>('all')
  const { data, isLoading } = useSoftphoneCallHistory(
    { actor_id: dispatcher?.dispatcher_id, limit: 50 },
    isOpen && isEligible
  )
  const patientPhoneNumbers = useMemo(
    () =>
      [...attemptedCalls, ...(data?.calls || [])].flatMap(
        getCallPhoneCandidates
      ),
    [attemptedCalls, data?.calls]
  )
  const contactLookup = useSoftphoneContactLookup(
    patientPhoneNumbers,
    isOpen && isEligible
  )

  const calls = useMemo(() => {
    const query = search.trim().toLowerCase()
    const backendCalls = data?.calls || []
    const backendCallIds = new Set(backendCalls.map(call => call.call_id))
    const mergedCalls = dedupeHistoryCalls(
      [
        ...attemptedCalls.filter(call => !backendCallIds.has(call.call_id)),
        ...backendCalls,
      ],
      contactLookup
    ).sort((a, b) => getCallTime(b) - getCallTime(a))

    return mergedCalls.filter(call => {
      const direction = getDirection(call)
      const matchesFilter =
        filter === 'all' ||
        (filter === 'missed' && isUnsuccessfulCallStatus(call.status)) ||
        direction === filter
      const searchable = [
        resolveCallContactName(call, contactLookup),
        getDisplayTarget(call),
        call.from,
        call.to,
        call.caller_id,
        call.dial_target,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      const matchesSearch = !query || searchable.includes(query)

      return matchesFilter && matchesSearch
    })
  }, [attemptedCalls, contactLookup, data?.calls, filter, search])

  return (
    <div className="font-family-sans flex min-h-0 flex-1 flex-col">
      <SearchBox value={search} onChange={setSearch} />

      <div className="mx-4 mt-2 grid shrink-0 grid-cols-4 bg-[#fbfbfb]">
        {historyFilters.map(item => (
          <button
            key={item.key}
            onClick={() => setFilter(item.key)}
            className={`h-10 border-b text-sm transition-colors cursor-pointer ${
              filter === item.key
                ? item.key === 'missed'
                  ? 'border-[#FA5252] text-[#FA5252]'
                  : 'border-primary font-medium text-primary'
                : 'border-transparent text-[#777777] hover:text-primary hover:bg-blue-100/50'
            }`}
            style={{
              fontSize: '14px',
              fontWeight: filter === item.key ? 500 : 400,
              color:
                filter === item.key
                  ? item.key === 'missed'
                    ? '#FA5252'
                    : '#254769'
                  : '#797676',
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <LoaderCircle size={28} className="animate-spin text-primary" />
          </div>
        ) : calls.length === 0 ? (
          <EmptyState
            title="No call history"
            subtitle="Call logs will appear here"
          />
        ) : (
          <div>
            {calls.map(call => {
              const target = resolveCallContactName(call, contactLookup)
              const responderName = resolveCallResponderName(
                call,
                contactLookup
              )
              const startedAt = call.started_at || call.created_at
              const callableNumber = getCallableHistoryNumber(call)
              return (
                <React.Fragment key={call.call_id}>
                  <div className="grid grid-cols-[28px_minmax(0,1fr)_112px] items-center gap-3 py-4">
                    {getHistoryIcon(call)}
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-[#333333]">
                        {target}
                      </p>
                      <p className="mt-1 truncate text-sm text-[#20232a]">
                        {getHistoryDetail(call, startedAt)}
                      </p>
                      {responderName ? (
                        <p className="mt-1 truncate text-xs font-medium text-[#0d8f45]">
                          {responderName}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex min-w-0 items-center justify-end gap-2">
                      {callableNumber ? (
                        <button
                          type="button"
                          onClick={() => void startDialpadCall(callableNumber)}
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#d9f3e4] bg-[#f3fff8] text-[#0d8f45] transition-colors hover:bg-[#e6f8ee]"
                          aria-label={`Call ${target}`}
                          title={`Call ${target}`}
                        >
                          <PhoneCall size={15} />
                        </button>
                      ) : null}
                      <div className="flex min-w-0 flex-col items-end gap-1 text-right">
                        <span
                          className={`max-w-full truncate rounded border px-2 py-0.5 text-[10px] font-semibold leading-4 ${getCallCategoryClasses(call)}`}
                          title={getCallCategory(call)}
                        >
                          {getCallCategory(call)}
                        </span>
                        <span className="max-w-full truncate text-[10px] capitalize leading-4 text-[#6b7280]">
                          {getCallStatusLabel(call)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <hr className="border-t border-[#e6e8eb]" />
                </React.Fragment>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

const DialpadView = () => {
  const {
    dialpadNumber,
    appendDialpadDigit,
    pasteDialpadNumber,
    deleteDialpadDigit,
    clearDialpadNumber,
    startDialpadCall,
    registrationStatus,
    isEligible,
  } = useSoftphone()
  const debouncedDialpadNumber = useDebounce(dialpadNumber, 300)
  const contactLookup = useSoftphoneContactLookup(
    [debouncedDialpadNumber],
    isEligible && normalizePhoneForLookup(debouncedDialpadNumber).length >= 7
  )
  const longPressTimeoutRef = useRef<number | null>(null)
  const longPressTriggeredRef = useRef(false)
  const dialpadContactName = useMemo(
    () => resolvePhoneContactName(dialpadNumber, contactLookup),
    [contactLookup, dialpadNumber]
  )

  const clearLongPressTimer = () => {
    if (longPressTimeoutRef.current === null) return

    window.clearTimeout(longPressTimeoutRef.current)
    longPressTimeoutRef.current = null
  }

  React.useEffect(() => {
    return () => {
      if (longPressTimeoutRef.current !== null) {
        window.clearTimeout(longPressTimeoutRef.current)
      }
    }
  }, [])

  const handleKeyPointerDown = (digit: string) => {
    if (digit !== '0') return

    longPressTriggeredRef.current = false
    clearLongPressTimer()
    longPressTimeoutRef.current = window.setTimeout(() => {
      longPressTimeoutRef.current = null
      longPressTriggeredRef.current = true
      appendDialpadDigit('+')
    }, 500)
  }

  const handleKeyPointerEnd = () => {
    clearLongPressTimer()
  }

  const handleKeyClick = (digit: string) => {
    if (digit === '0' && longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false
      return
    }

    appendDialpadDigit(digit)
  }

  const canCallDialpadNumber =
    Boolean(dialpadNumber) && isEligible && registrationStatus === 'registered'

  const handleDialpadCall = () => {
    if (!canCallDialpadNumber) return

    void startDialpadCall(dialpadNumber)
  }

  React.useEffect(() => {
    const handleKeyboardInput = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const targetTag = target?.tagName?.toLowerCase()
      const isEditableTarget =
        target?.isContentEditable ||
        targetTag === 'input' ||
        targetTag === 'textarea' ||
        targetTag === 'select'

      if (isEditableTarget || event.ctrlKey || event.metaKey || event.altKey) {
        return
      }

      if (/^[0-9]$/.test(event.key) || event.key === '*' || event.key === '#') {
        event.preventDefault()
        appendDialpadDigit(event.key)
        return
      }

      if (event.key === '+') {
        event.preventDefault()
        appendDialpadDigit('+')
        return
      }

      if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault()
        deleteDialpadDigit()
        return
      }

      if (event.key === 'Enter') {
        event.preventDefault()
        handleDialpadCall()
      }
    }

    window.addEventListener('keydown', handleKeyboardInput)
    return () => window.removeEventListener('keydown', handleKeyboardInput)
  }, [appendDialpadDigit, deleteDialpadDigit, handleDialpadCall])

  const handleDialpadPaste = (event: React.ClipboardEvent) => {
    event.preventDefault()
    pasteDialpadNumber(event.clipboardData.getData('text'))
  }

  const handleClipboardPaste = async () => {
    try {
      const clipboardText = await navigator.clipboard?.readText()
      pasteDialpadNumber(clipboardText || '')
    } catch {
      // Browser clipboard permissions can block button-driven reads; keyboard paste still works.
    }
  }

  return (
    <form
      onSubmit={event => event.preventDefault()}
      onPaste={handleDialpadPaste}
      className="min-h-0 flex-1 overflow-y-auto px-4 pb-4"
    >
      <div className="flex min-h-full flex-col justify-end">
        <div className="mb-2 flex min-h-24 flex-col items-center justify-center">
          <div
            tabIndex={0}
            role="textbox"
            aria-label="Dialpad number"
            aria-readonly="true"
            className={`flex min-h-16 max-w-full items-center justify-center break-all bg-white text-center text-[42px] font-medium leading-tight ${
              dialpadNumber ? 'text-[#4a4a4a]' : 'text-[#969696]'
            }`}
          >
            {formatDialpadNumber(dialpadNumber)}
          </div>
          <div className="mt-1 h-5 max-w-full truncate text-sm font-medium text-primary">
            {dialpadNumber && dialpadContactName ? dialpadContactName : ''}
          </div>
        </div>

        <div className="mb-8 flex items-center justify-between">
          <button
            type="button"
            onClick={handleClipboardPaste}
            className="flex h-10 w-10 items-center justify-center rounded-full text-[#6c6c6c] hover:bg-gray-100 cursor-pointer"
            aria-label="Paste number"
            title="Paste number"
          >
            <ClipboardPaste size={21} />
          </button>
          <button
            type="button"
            onClick={dialpadNumber ? deleteDialpadDigit : clearDialpadNumber}
            className="flex h-10 w-10 items-center justify-center rounded-full text-[#6c6c6c] hover:bg-gray-100 cursor-pointer"
            aria-label="Delete digit"
          >
            <Delete size={22} />
          </button>
        </div>

        <div className="mx-auto mb-4 grid w-full grid-cols-3 gap-2">
          {dialpadKeys.map(key => (
            <button
              type="button"
              key={key.digit}
              onClick={() => handleKeyClick(key.digit)}
              onContextMenu={event => {
                if (key.digit === '0') event.preventDefault()
              }}
              onPointerCancel={handleKeyPointerEnd}
              onPointerDown={() => handleKeyPointerDown(key.digit)}
              onPointerLeave={handleKeyPointerEnd}
              onPointerUp={handleKeyPointerEnd}
              className="flex h-24 flex-col items-center justify-center rounded-lg border border-[#e2e2e2] bg-white text-[#2a2a2a] transition-colors hover:bg-[#f8fafc] cursor-pointer"
              aria-label={
                key.digit === '0' ? '0, press and hold for plus' : key.digit
              }
            >
              <span className="text-2xl font-bold">{key.digit}</span>
              {!['*', '#'].includes(key.digit) && (
                <span
                  className={`h-4 text-xs text-[#8b8b8b] ${
                    key.letters ? '' : 'invisible'
                  }`}
                >
                  {key.letters || '-'}
                </span>
              )}
            </button>
          ))}
        </div>

        <button
          type="button"
          disabled={!canCallDialpadNumber}
          onClick={handleDialpadCall}
          title="Call number"
          className="flex h-[60px] items-center justify-center gap-2 rounded border border-dashed border-primary bg-[#eaf2ff] text-sm font-semibold text-primary disabled:cursor-not-allowed disabled:opacity-70 cursor-pointer"
        >
          <Phone size={20} />
          Call
        </button>
      </div>
    </form>
  )
}

const ContactsView = () => {
  const [search, setSearch] = useState('')
  const [selectedTab, setSelectedTab] = useState<ContactTab>('all')
  const { startDialpadCall, startResponderCall } = useSoftphone()
  const { dispatcher } = useDispatcher()
  const currentPrecinctId = dispatcher?.precinct_id
  const { data: respondersData, isLoading: loadingResponders } =
    useAllResponders({
      pageNumber: 1,
      numberPerPage: 100,
      precinct_id: currentPrecinctId,
    })
  const { data: hospitals = [], isLoading: loadingHospitals } = useAllHospitals(
    {
      pageNumber: 1,
      numberPerPage: 100,
      precinct_id: currentPrecinctId,
    }
  )
  const { data: ambulanceData, isLoading: loadingAmbulances } =
    useAmbulanceProviders({ limit: 100, precinct_id: currentPrecinctId })

  const contacts = useMemo<SoftphoneContact[]>(() => {
    const responderContacts =
      respondersData?.items
        ?.filter(responder =>
          matchesCurrentPrecinct(responder, currentPrecinctId)
        )
        .map(responder => ({
          id: responder.responder_id,
          name:
            [responder.first_name, responder.last_name]
              .filter(Boolean)
              .join(' ') ||
            responder.phone_number ||
            'Responder',
          phoneNumber: responder.phone_number,
          type: 'responders' as ContactTab,
          subtitle: responder.responder_type,
          status: responder.status,
          responder,
        })) || []

    const hospitalContacts = hospitals
      .filter((hospital: any) =>
        matchesCurrentPrecinct(hospital, currentPrecinctId)
      )
      .map((hospital: any, index: number) => ({
        id: hospital.hospital_id || hospital.id || `hospital-${index}`,
        name: hospital.name || hospital.hospital_name || 'Hospital',
        phoneNumber:
          hospital.phoneNumber ||
          hospital.phone_number ||
          hospital.contact?.phone ||
          hospital.contact_phone,
        type: 'hospitals' as ContactTab,
        subtitle: hospital.availability_status || hospital.state,
        status: hospital.availability_status,
      }))

    const ambulanceProviders = Array.isArray((ambulanceData as any)?.items)
      ? (ambulanceData as any).items
      : Array.isArray((ambulanceData as any)?.ambulance_providers)
        ? (ambulanceData as any).ambulance_providers
        : Array.isArray((ambulanceData as any)?.providers)
          ? (ambulanceData as any).providers
          : Array.isArray(ambulanceData)
            ? ambulanceData
            : []

    const ambulanceContacts = ambulanceProviders
      .filter((provider: any) =>
        matchesCurrentPrecinct(provider, currentPrecinctId)
      )
      .map((provider: any, index: number) => ({
        id:
          provider.provider_id ||
          provider.ambulance_provider_id ||
          provider.id ||
          `ambulance-${index}`,
        name:
          provider.provider_name ||
          provider.company_name ||
          provider.name ||
          'Ambulance Provider',
        phoneNumber:
          provider.contact_phone_number ||
          provider.phone_number ||
          provider.contact_phone ||
          provider.dispatch_phone ||
          provider.dispatch_phone_number ||
          provider.contact?.phone,
        type: 'ambulances' as ContactTab,
        subtitle: provider.service_level || provider.status,
        status: provider.status,
      }))

    return [...responderContacts, ...hospitalContacts, ...ambulanceContacts]
  }, [ambulanceData, currentPrecinctId, hospitals, respondersData?.items])

  const visibleContacts = useMemo(() => {
    const query = search.trim().toLowerCase()
    return contacts.filter(contact => {
      const matchesTab = selectedTab === 'all' || contact.type === selectedTab
      const searchable = [contact.name, contact.phoneNumber, contact.subtitle]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return matchesTab && (!query || searchable.includes(query))
    })
  }, [contacts, search, selectedTab])

  const isLoading = loadingResponders || loadingHospitals || loadingAmbulances

  const isContactCallable = (contact: SoftphoneContact) =>
    Boolean(contact.responder || contact.phoneNumber)

  const handleCallContact = (contact: SoftphoneContact) => {
    if (!isContactCallable(contact)) return

    if (contact.responder) {
      void startResponderCall({
        responderId: contact.responder.responder_id,
        displayName: contact.name,
        phoneNumber: contact.phoneNumber,
        caseId: contact.responder.responding_to || undefined,
        status: contact.responder.status,
      })
      return
    }

    if (contact.phoneNumber) {
      void startDialpadCall(contact.phoneNumber)
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <SearchBox value={search} onChange={setSearch} />
      <div className="mx-4 mt-2 grid shrink-0 grid-cols-4 bg-[#fbfbfb]">
        {[
          { key: 'all', label: 'All' },
          { key: 'responders', label: 'Responders' },
          { key: 'hospitals', label: 'Hospitals' },
          { key: 'ambulances', label: 'Ambulances' },
        ].map(item => (
          <button
            key={item.key}
            onClick={() => setSelectedTab(item.key as ContactTab)}
            className={`h-10 border-b text-sm cursor-pointer ${
              selectedTab === item.key
                ? 'border-primary font-medium text-primary'
                : 'border-transparent text-[#777777]'
            }`}
            style={{ fontSize: '12px' }}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <LoaderCircle size={28} className="animate-spin text-primary" />
          </div>
        ) : visibleContacts.length === 0 ? (
          <EmptyState
            title="No contacts"
            subtitle="Contacts will appear here"
            icon={<ContactRound size={50} className="text-[#969696]" />}
          />
        ) : (
          <div className="space-y-3">
            {visibleContacts.map(contact => {
              const canCallContact = isContactCallable(contact)
              return (
                <div
                  key={`${contact.type}-${contact.id}`}
                  className="grid grid-cols-[1fr_auto] items-center gap-3 rounded border border-[#e5e7eb] px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-[#333333]">
                      {contact.name}
                    </p>
                    <p className="mt-1 truncate text-xs text-[#6b7280]">
                      {[contact.phoneNumber, contact.subtitle, contact.status]
                        .filter(Boolean)
                        .join(' - ')}
                    </p>
                  </div>
                  <button
                    disabled={!canCallContact}
                    onClick={() => handleCallContact(contact)}
                    title={
                      canCallContact
                        ? `Call ${contact.name}`
                        : 'No callable number available'
                    }
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-[#eaf2ff] text-primary disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label={
                      canCallContact
                        ? `Call ${contact.name}`
                        : `${contact.name} cannot be called`
                    }
                  >
                    <Phone size={17} />
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

const CallQueueBanner = ({ count }: { count: number }) => {
  if (count <= 0) return null

  return (
    <div
      className="mx-4 mt-3 flex h-10 shrink-0 items-center gap-2 rounded bg-[#fff1e4] px-3 text-[#ff7a1a]"
      title={`${count} inbound call${count === 1 ? '' : 's'} currently waiting in the Asterisk dispatch queue.`}
      aria-label={`${count} inbound call${count === 1 ? '' : 's'} waiting in the dispatch queue`}
    >
      <Clock3 size={17} />
      <span className="text-base font-medium">
        {count} call{count === 1 ? '' : 's'} in queue
      </span>
    </div>
  )
}

const QueueMiniStatus = ({
  queue,
  fallbackCalls,
  fallbackDispatcherPosition,
  fallbackDispatcherCount,
}: {
  queue?: SoftphoneQueueStatus
  fallbackCalls: SoftphoneCallSession[]
  fallbackDispatcherPosition?: number
  fallbackDispatcherCount: number
}) => {
  const fallbackQueuedCalls = fallbackCalls.filter(call =>
    ['initiating', 'dialing', 'ringing', 'answered', 'held'].includes(
      String(call.status)
    )
  )
  const queueCount = queue?.callerCount ?? fallbackQueuedCalls.length
  const dispatcherCount = queue?.dispatcherCount ?? fallbackDispatcherCount
  const dispatcherPosition =
    queue?.dispatcherPosition ?? fallbackDispatcherPosition

  const dispatcherText =
    dispatcherPosition && dispatcherCount
      ? `D ${dispatcherPosition}/${dispatcherCount}`
      : dispatcherCount > 0
        ? `${dispatcherCount} disp`
        : '0 disp'
  const queueSource = queue ? 'Asterisk queue' : 'Local activity fallback'
  const checkedAt = queue?.checkedAt
    ? new Date(queue.checkedAt).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null
  const tooltipLines = [
    `${queueCount} caller${queueCount === 1 ? '' : 's'} waiting`,
    `${dispatcherCount} available dispatcher${dispatcherCount === 1 ? '' : 's'}`,
    dispatcherPosition
      ? `Your queue order: ${dispatcherPosition} of ${dispatcherCount || dispatcherPosition}`
      : 'Your queue order is not active',
    `Source: ${queueSource}`,
    checkedAt ? `Checked ${checkedAt}` : null,
    queue?.degraded
      ? `Limited queue data${queue.error ? `: ${queue.error}` : ''}`
      : null,
  ].filter(Boolean)
  const tooltipText = tooltipLines.join('. ')

  return (
    <div
      tabIndex={0}
      title={tooltipText}
      aria-label={tooltipText}
      className="group absolute bottom-2 right-3 z-10 rounded bg-white/95 px-2 py-1 text-[10px] font-medium leading-none text-[#6b7280] shadow-sm ring-1 ring-[#e5e7eb] outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <span>
        Queue {queueCount} - {dispatcherText}
      </span>
      <div className="pointer-events-none absolute bottom-full right-0 mb-2 hidden w-64 rounded border border-[#dfe3ea] bg-white p-3 text-left text-xs font-normal leading-5 text-[#4b5563] shadow-lg group-focus:block group-hover:block">
        <p className="font-semibold text-[#333333]">Dispatch queue</p>
        <p className="mt-1">
          {queueCount} caller{queueCount === 1 ? '' : 's'} waiting.{' '}
          {dispatcherCount} dispatcher{dispatcherCount === 1 ? '' : 's'} ready.
        </p>
        <p className="mt-1">
          {dispatcherPosition
            ? `You are ${dispatcherPosition} of ${dispatcherCount || dispatcherPosition} in the active Asterisk queue.`
            : `You are not currently placed in the active Asterisk queue order.`}
        </p>
        <p className="mt-1 text-[#6b7280]">
          {queueSource}
          {checkedAt ? ` - checked ${checkedAt}` : ''}
        </p>
        {queue?.degraded ? (
          <p className="mt-1 text-[#9a4a00]">
            Limited queue data{queue.error ? `: ${queue.error}` : ''}
          </p>
        ) : null}
      </div>
    </div>
  )
}

const CallControlButton = ({
  label,
  icon,
  onClick,
  tone = 'default',
  disabled = false,
}: {
  label: string
  icon: React.ReactNode
  onClick?: () => void
  tone?: 'default' | 'danger' | 'success' | 'warning'
  disabled?: boolean
}) => {
  const toneClass =
    tone === 'danger'
      ? 'border-[#ff5263] bg-[#fff6f7] text-[#ff5263]'
      : tone === 'success'
        ? 'border-[#12a84f] bg-[#f2fff7] text-[#12a84f]'
        : tone === 'warning'
          ? 'border-[#ff8a22] bg-[#fff8f1] text-[#ff8a22]'
          : 'border-[#cfcfcf] bg-white text-[#333333]'

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex w-[76px] flex-col items-center gap-2 disabled:cursor-not-allowed disabled:opacity-35"
    >
      <span
        className={`flex h-[68px] w-[68px] items-center justify-center rounded-2xl border ${toneClass}`}
      >
        {icon}
      </span>
      <span className="text-sm text-[#4a4a4a]">{label}</span>
    </button>
  )
}

const CallScreen = ({ queueCount }: { queueCount: number }) => {
  const {
    activeCall,
    answerIncomingCall,
    hangupCall,
    isAnsweringIncomingCall,
    rejectIncomingCall,
    toggleHold,
    toggleMute,
    transferActiveCall,
  } = useSoftphone()
  const activeCallPatientPhoneNumbers = useMemo(
    () => getActiveCallPhoneCandidates(activeCall),
    [activeCall]
  )
  const contactLookup = useSoftphoneContactLookup(
    activeCallPatientPhoneNumbers,
    Boolean(activeCall)
  )
  const timer = useCallTimer(activeCall)
  const [showTransfer, setShowTransfer] = useState(false)
  const [transferDestination, setTransferDestination] = useState('')
  const contactDisplay = useMemo(
    () =>
      activeCall
        ? getActiveCallContactDisplay(activeCall, contactLookup)
        : undefined,
    [activeCall, contactLookup]
  )

  if (!activeCall) return null

  const isIncoming = activeCall.status === 'ringing'
  const isDialing =
    activeCall.status === 'initiating' || activeCall.status === 'dialing'
  const isHeld = activeCall.status === 'held' || activeCall.isHeld
  const connected = activeCall.status === 'answered' || isHeld
  const statusLabel = isIncoming
    ? 'Incoming.....'
    : isDialing
      ? 'Dialing........'
      : isHeld
        ? 'On Hold'
        : 'Connected'
  const statusColor = isHeld ? 'text-[#ff8a22]' : 'text-[#12a84f]'

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <CallQueueBanner count={queueCount} />

      <div className="flex min-h-[360px] flex-1 flex-col items-center justify-center px-6">
        <div className="mb-8 text-center">
          {isIncoming ? (
            <PhoneIncoming size={22} className="mx-auto text-[#12a84f]" />
          ) : connected ? (
            <UserRound
              size={23}
              className={`mx-auto ${isHeld ? 'text-[#ff8a22]' : 'text-[#12a84f]'}`}
            />
          ) : null}
          <p
            className={`mt-2 text-base font-semibold ${
              isDialing ? 'text-[#333333]' : statusColor
            }`}
          >
            {statusLabel}
          </p>
        </div>

        <p className="max-w-full break-all text-center text-[42px] font-medium leading-tight text-[#4a4a4a]">
          {contactDisplay?.name}
        </p>

        {contactDisplay?.detail ? (
          <p className="mt-3 max-w-full break-all text-center text-base text-[#555555]">
            {contactDisplay.detail}
          </p>
        ) : connected ? (
          <p className="mt-5 text-[26px] font-semibold text-[#666666]">
            {timer}
          </p>
        ) : null}
        {contactDisplay?.detail && connected ? (
          <p className="mt-5 text-[26px] font-semibold text-[#666666]">
            {timer}
          </p>
        ) : null}
      </div>

      <div className="mb-14 flex shrink-0 items-center justify-center gap-5">
        {isIncoming ? (
          <>
            <CallControlButton
              label="Reject"
              tone="danger"
              icon={<PhoneOff size={26} />}
              onClick={() => void rejectIncomingCall()}
            />
            <CallControlButton
              label={isAnsweringIncomingCall ? 'Answering' : 'Answer'}
              tone="success"
              icon={<Phone size={26} />}
              disabled={isAnsweringIncomingCall}
              onClick={() => void answerIncomingCall()}
            />
          </>
        ) : (
          <>
            <CallControlButton
              label={activeCall.isMuted ? 'Unmute' : 'Mute'}
              tone={activeCall.isMuted ? 'danger' : 'default'}
              icon={
                activeCall.isMuted ? <MicOff size={25} /> : <Mic size={25} />
              }
              disabled={isDialing}
              onClick={toggleMute}
            />
            <CallControlButton
              label={isHeld ? 'Resume' : 'Hold'}
              tone={isHeld ? 'warning' : 'default'}
              icon={isHeld ? <Play size={24} /> : <Pause size={24} />}
              disabled={isDialing}
              onClick={() => void toggleHold()}
            />
            <CallControlButton
              label="Transfer"
              icon={<Forward size={25} />}
              disabled={isDialing}
              onClick={() => setShowTransfer(current => !current)}
            />
            <CallControlButton
              label="End Call"
              tone="danger"
              icon={<PhoneOff size={26} />}
              onClick={() => void hangupCall()}
            />
          </>
        )}
      </div>
      {showTransfer && (
        <div className="mx-4 mb-6 flex shrink-0 gap-2">
          <input
            value={transferDestination}
            onChange={event => setTransferDestination(event.target.value)}
            placeholder="Extension, SIP URI, or phone number"
            className="h-10 min-w-0 flex-1 rounded border border-[#cfcfcf] px-3 text-sm outline-none"
          />
          <button
            onClick={() => void transferActiveCall(transferDestination)}
            className="h-10 rounded bg-primary px-4 text-sm font-semibold text-white"
          >
            Transfer
          </button>
        </div>
      )}
    </div>
  )
}

const ReadinessBanner = () => {
  const {
    error,
    isEligible,
    readinessMessage,
    registrationStatus,
    realtimeError,
    realtimeStatus,
    retryConnection,
  } = useSoftphone()

  const hasRegistrationIssue = registrationStatus !== 'registered'
  const realtimeMessage =
    isEligible && registrationStatus === 'registered'
      ? getRealtimeStatusMessage(realtimeStatus, realtimeError)
      : null
  const message =
    error || (hasRegistrationIssue ? readinessMessage : realtimeMessage)
  const displayMessage = humanizeSoftphoneMessage(message)

  if (!displayMessage) return null

  const isWarning =
    !isEligible ||
    Boolean(error) ||
    realtimeStatus === 'disconnected' ||
    realtimeStatus === 'error' ||
    realtimeStatus === 'unavailable'

  return (
    <div
      className={`mx-4 mt-3 flex shrink-0 items-center justify-between gap-3 rounded px-3 py-2 text-sm ${
        isWarning ? 'bg-[#fff1e4] text-[#9a4a00]' : 'bg-[#f4f7fb] text-primary'
      }`}
    >
      <span className="min-w-0 truncate" title={message || undefined}>
        {displayMessage}
      </span>
      <button
        onClick={retryConnection}
        className="shrink-0 rounded p-1 hover:bg-white/60 cursor-pointer"
        aria-label="Retry softphone connection"
      >
        <RefreshCw size={16} />
      </button>
    </div>
  )
}

interface SoftphoneWidgetProps {
  anchorRef: RefObject<HTMLElement | null>
}

export const SoftphoneWidget = ({ anchorRef }: SoftphoneWidgetProps) => {
  const { activeCall, activeTab, closeWidget, isEligible, isOpen } =
    useSoftphone()
  const { dispatcher } = useDispatcher()
  const { data: activeCallSummary } = useActiveSoftphoneCalls(
    100,
    isOpen && isEligible
  )
  const { data: queueStatus } = useSoftphoneQueueStatus(
    dispatcher?.dispatcher_id,
    isOpen && isEligible
  )
  const { data: shiftsData } = useShifts(
    {
      limit: 100,
      sortBy: 'from',
      sortOrder: 'desc',
    },
    {
      enabled: isOpen && isEligible,
      refetchInterval: 60 * 1000,
    }
  )
  const activeDispatcherShifts = useMemo(
    () =>
      (shiftsData?.items || [])
        .filter(
          shift =>
            shift.status === 'in-progress' &&
            shift.clock_in_status &&
            !shift.clock_out_status
        )
        .sort((a, b) =>
          String(a.clock_in_time || a.from).localeCompare(
            String(b.clock_in_time || b.from)
          )
        ),
    [shiftsData?.items]
  )
  const dispatcherQueuePosition = activeDispatcherShifts.findIndex(
    shift => shift.dispatcher_id === dispatcher?.dispatcher_id
  )
  const activeCalls = activeCallSummary?.calls || []
  const effectiveQueueStatus = queueStatus || activeCallSummary?.queue
  const fallbackQueueCount = activeCalls.length || (activeCall ? 1 : 0)
  const queueCount = effectiveQueueStatus?.callerCount ?? fallbackQueueCount

  const [position, setPosition] = useState({
    top: 0,
    right: SOFTPHONE_VIEWPORT_MARGIN,
    height: SOFTPHONE_PANEL_MAX_HEIGHT,
  })

  useLayoutEffect(() => {
    if (!isOpen || !anchorRef.current) return

    const updatePosition = () => {
      const rect = anchorRef.current?.getBoundingClientRect()
      if (!rect) return

      const top = rect.bottom + SOFTPHONE_PANEL_GAP
      const panelWidth = Math.min(
        SOFTPHONE_PANEL_WIDTH,
        window.innerWidth - SOFTPHONE_VIEWPORT_MARGIN * 2
      )
      const rawRight = window.innerWidth - rect.right
      const maxRight = Math.max(
        SOFTPHONE_VIEWPORT_MARGIN,
        window.innerWidth - panelWidth - SOFTPHONE_VIEWPORT_MARGIN
      )
      const right = Math.min(
        Math.max(SOFTPHONE_VIEWPORT_MARGIN, rawRight),
        maxRight
      )
      const height = Math.max(
        0,
        Math.min(
          SOFTPHONE_PANEL_MAX_HEIGHT,
          window.innerHeight - top - SOFTPHONE_VIEWPORT_MARGIN
        )
      )

      setPosition({ top, right, height })
    }

    updatePosition()

    // Update position on scroll or resize
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen, anchorRef])

  if (!isOpen) return null

  return (
    <aside
      className="font-family-sans fixed z-90 flex w-[472px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg border border-[#dfdfdf] bg-white shadow-2xl"
      style={{
        top: `${position.top}px`,
        right: `${position.right}px`,
        height: `${position.height}px`,
      }}
    >
      <div className="font-family-heading flex shrink-0 items-center justify-between border-b border-[#dfdfdf] px-4 py-3">
        <div className="text-lg font-bold text-[#413F3F]">Soft Phone</div>
        <button
          onClick={closeWidget}
          className="p-1 rounded-lg hover:bg-gray-100 cursor-pointer"
          aria-label="Close softphone"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {activeCall ? (
        <CallScreen queueCount={queueCount} />
      ) : (
        <>
          <SoftphoneTopTabs />
          <ReadinessBanner />
          <div className="relative flex min-h-0 flex-1 flex-col pb-7">
            {activeTab === 'history' && <HistoryView />}
            {activeTab === 'dialpad' && <DialpadView />}
            {activeTab === 'contacts' && <ContactsView />}
            <QueueMiniStatus
              queue={effectiveQueueStatus}
              fallbackCalls={activeCalls}
              fallbackDispatcherPosition={
                dispatcherQueuePosition >= 0
                  ? dispatcherQueuePosition + 1
                  : undefined
              }
              fallbackDispatcherCount={activeDispatcherShifts.length}
            />
          </div>
        </>
      )}
    </aside>
  )
}

export const SoftphoneHeaderButton = () => {
  const {
    activeCall,
    isEligible,
    openWidget,
    registrationStatus,
    realtimeError,
    realtimeStatus,
    readinessMessage,
  } = useSoftphone()

  const buttonRef = useRef<HTMLButtonElement>(null)
  const isRegistered = registrationStatus === 'registered'
  const isRealtimeConnected = realtimeStatus === 'connected'
  const realtimeMessage = getRealtimeStatusMessage(
    realtimeStatus,
    realtimeError
  )
  const buttonTitle =
    isRegistered && !isRealtimeConnected && realtimeMessage
      ? `${readinessMessage}. ${realtimeMessage}`
      : readinessMessage

  const dotClass = activeCall
    ? 'bg-[#ff5263]'
    : !isEligible
      ? 'bg-[#9ca3af]'
      : registrationStatus === 'error' || registrationStatus === 'unavailable'
        ? 'bg-[#ff5263]'
        : isRegistered && isRealtimeConnected
          ? 'bg-[#12a84f]'
          : 'bg-[#ffb020]'

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => openWidget(activeCall ? 'history' : undefined)}
        className="relative rounded-full p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 cursor-pointer"
        aria-label="Open softphone"
        title={buttonTitle}
      >
        <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-gray-40">
          {registrationStatus === 'connecting' ? (
            <LoaderCircle size={20} className="animate-spin text-primary" />
          ) : activeCall ? (
            <PhoneCall size={20} className="text-[#ff5263]" />
          ) : (
            <Phone size={20} className="text-primary" />
          )}
          <span
            className={`absolute right-1 top-1 h-2.5 w-2.5 rounded-full border border-white ${dotClass}`}
          />
        </div>
      </button>
      <SoftphoneWidget anchorRef={buttonRef} />
    </div>
  )
}
